/**
 * @fileoverview i18n configuration with auto-detection and language persistence.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import dayjs from 'dayjs';

// Import dayjs locales
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/hi';
import 'dayjs/locale/vi';
import 'dayjs/locale/ms';
import 'dayjs/locale/es';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import 'dayjs/locale/ru';
import 'dayjs/locale/ja';
import 'dayjs/locale/it';
import 'dayjs/locale/zh-hk';
import 'dayjs/locale/pa-in';
import 'dayjs/locale/el';
import 'dayjs/locale/tl-ph';
import 'dayjs/locale/pt';
import 'dayjs/locale/ko';
import 'dayjs/locale/id';
import 'dayjs/locale/tr';
import 'dayjs/locale/th';
import 'dayjs/locale/uk';
import 'dayjs/locale/nl';
import 'dayjs/locale/fi';
import 'dayjs/locale/sv';
import 'dayjs/locale/nb';
import 'dayjs/locale/da';
import 'dayjs/locale/bn';
import 'dayjs/locale/pl';
import 'dayjs/locale/cs';
import 'dayjs/locale/ur';
import 'dayjs/locale/fa';
import 'dayjs/locale/sw';
import 'dayjs/locale/ta';
import 'dayjs/locale/te';
import 'dayjs/locale/mr';
import 'dayjs/locale/gu';
import 'dayjs/locale/kn';
import 'dayjs/locale/ml';
import 'dayjs/locale/hu';
import 'dayjs/locale/ro';
import 'dayjs/locale/bg';
import 'dayjs/locale/sr';
import 'dayjs/locale/hr';
import 'dayjs/locale/my';
import 'dayjs/locale/km';
import 'dayjs/locale/lo';
import 'dayjs/locale/ne';
import 'dayjs/locale/he';
import 'dayjs/locale/ca';
import 'dayjs/locale/af';
import 'dayjs/locale/si';
import 'dayjs/locale/az';
import 'dayjs/locale/uz';
import 'dayjs/locale/kk';
import 'dayjs/locale/am';
import 'dayjs/locale/sk';
import 'dayjs/locale/sl';
import 'dayjs/locale/lt';
import 'dayjs/locale/lv';
import 'dayjs/locale/et';
import 'dayjs/locale/sq';
import 'dayjs/locale/ka';
import 'dayjs/locale/mi';
import 'dayjs/locale/mn';
// Note: Samoan (sm) doesn't have a dayjs locale - falls back to English
// Note: Norwegian uses 'nb' (Bokmål) dayjs locale
// Note: Cantonese (yue) uses zh-hk dayjs locale
// Note: Hausa (ha), Yoruba (yo), Zulu (zu), Somali (so), Odia (or) use English fallback for dayjs

import en from './locales/en.json';
import zh from './locales/zh.json';
import hi from './locales/hi.json';
import vi from './locales/vi.json';
import ms from './locales/ms.json';
import sm from './locales/sm.json';
import es from './locales/es.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import ar from './locales/ar.json';
import ru from './locales/ru.json';
import ja from './locales/ja.json';
import it from './locales/it.json';
import yue from './locales/yue.json';
import pa from './locales/pa.json';
import el from './locales/el.json';
import fil from './locales/fil.json';
import pt from './locales/pt.json';
import ko from './locales/ko.json';
import id from './locales/id.json';
import tr from './locales/tr.json';
import th from './locales/th.json';
import uk from './locales/uk.json';
import nl from './locales/nl.json';
import fi from './locales/fi.json';
import sv from './locales/sv.json';
import no from './locales/no.json';
import da from './locales/da.json';
import bn from './locales/bn.json';
import pl from './locales/pl.json';
import cs from './locales/cs.json';
import ur from './locales/ur.json';
import fa from './locales/fa.json';
import sw from './locales/sw.json';
import ta from './locales/ta.json';
import te from './locales/te.json';
import mr from './locales/mr.json';
import gu from './locales/gu.json';
import kn from './locales/kn.json';
import ml from './locales/ml.json';
import hu from './locales/hu.json';
import ro from './locales/ro.json';
import bg from './locales/bg.json';
import sr from './locales/sr.json';
import hr from './locales/hr.json';
import my from './locales/my.json';
import km from './locales/km.json';
import lo from './locales/lo.json';
import ne from './locales/ne.json';
import he from './locales/he.json';
import ca from './locales/ca.json';
import af from './locales/af.json';
import si from './locales/si.json';
import az from './locales/az.json';
import uz from './locales/uz.json';
import kk from './locales/kk.json';
import am from './locales/am.json';
import ha from './locales/ha.json';
import yo from './locales/yo.json';
import zu from './locales/zu.json';
import so from './locales/so.json';
import sk from './locales/sk.json';
import sl from './locales/sl.json';
import lt from './locales/lt.json';
import lv from './locales/lv.json';
import et from './locales/et.json';
import sq from './locales/sq.json';
import ka from './locales/ka.json';
import mi from './locales/mi.json';
import mn from './locales/mn.json';
import or from './locales/or.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', dayjsLocale: 'en', dir: 'ltr' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', dayjsLocale: 'zh-cn', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', dayjsLocale: 'hi', dir: 'ltr' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', dayjsLocale: 'vi', dir: 'ltr' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾', dayjsLocale: 'ms', dir: 'ltr' },
  { code: 'sm', name: 'Samoan', nativeName: 'Gagana Samoa', flag: '🇼🇸', dayjsLocale: 'en', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dayjsLocale: 'es', dir: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', dayjsLocale: 'de', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dayjsLocale: 'fr', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', dayjsLocale: 'ar', dir: 'rtl' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', dayjsLocale: 'ru', dir: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', dayjsLocale: 'ja', dir: 'ltr' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', dayjsLocale: 'it', dir: 'ltr' },
  { code: 'yue', name: 'Cantonese', nativeName: '廣東話', flag: '🇭🇰', dayjsLocale: 'zh-hk', dir: 'ltr' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳', dayjsLocale: 'pa-in', dir: 'ltr' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷', dayjsLocale: 'el', dir: 'ltr' },
  { code: 'fil', name: 'Filipino', nativeName: 'Filipino', flag: '🇵🇭', dayjsLocale: 'tl-ph', dir: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', dayjsLocale: 'pt', dir: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', dayjsLocale: 'ko', dir: 'ltr' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩', dayjsLocale: 'id', dir: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', dayjsLocale: 'tr', dir: 'ltr' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭', dayjsLocale: 'th', dir: 'ltr' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦', dayjsLocale: 'uk', dir: 'ltr' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', dayjsLocale: 'nl', dir: 'ltr' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮', dayjsLocale: 'fi', dir: 'ltr' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪', dayjsLocale: 'sv', dir: 'ltr' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴', dayjsLocale: 'nb', dir: 'ltr' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰', dayjsLocale: 'da', dir: 'ltr' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩', dayjsLocale: 'bn', dir: 'ltr' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱', dayjsLocale: 'pl', dir: 'ltr' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿', dayjsLocale: 'cs', dir: 'ltr' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰', dayjsLocale: 'ur', dir: 'rtl' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', flag: '🇮🇷', dayjsLocale: 'fa', dir: 'rtl' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪', dayjsLocale: 'sw', dir: 'ltr' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', dayjsLocale: 'ta', dir: 'ltr' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳', dayjsLocale: 'te', dir: 'ltr' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳', dayjsLocale: 'mr', dir: 'ltr' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳', dayjsLocale: 'gu', dir: 'ltr' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳', dayjsLocale: 'kn', dir: 'ltr' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳', dayjsLocale: 'ml', dir: 'ltr' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺', dayjsLocale: 'hu', dir: 'ltr' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴', dayjsLocale: 'ro', dir: 'ltr' },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български', flag: '🇧🇬', dayjsLocale: 'bg', dir: 'ltr' },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски', flag: '🇷🇸', dayjsLocale: 'sr', dir: 'ltr' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', flag: '🇭🇷', dayjsLocale: 'hr', dir: 'ltr' },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာဘာသာ', flag: '🇲🇲', dayjsLocale: 'my', dir: 'ltr' },
  { code: 'km', name: 'Khmer', nativeName: 'ភាសាខ្មែរ', flag: '🇰🇭', dayjsLocale: 'km', dir: 'ltr' },
  { code: 'lo', name: 'Lao', nativeName: 'ລາວ', flag: '🇱🇦', dayjsLocale: 'lo', dir: 'ltr' },
  { code: 'ne', name: 'Nepali', nativeName: 'नेपाली', flag: '🇳🇵', dayjsLocale: 'ne', dir: 'ltr' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱', dayjsLocale: 'he', dir: 'rtl' },
  { code: 'ca', name: 'Catalan', nativeName: 'Català', flag: '🏴󠁥󠁳󠁣󠁴󠁿', dayjsLocale: 'ca', dir: 'ltr' },
  { code: 'af', name: 'Afrikaans', nativeName: 'Afrikaans', flag: '🇿🇦', dayjsLocale: 'af', dir: 'ltr' },
  { code: 'si', name: 'Sinhala', nativeName: 'සිංහල', flag: '🇱🇰', dayjsLocale: 'si', dir: 'ltr' },
  { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycan', flag: '🇦🇿', dayjsLocale: 'az', dir: 'ltr' },
  { code: 'uz', name: 'Uzbek', nativeName: "O'zbek", flag: '🇺🇿', dayjsLocale: 'uz', dir: 'ltr' },
  { code: 'kk', name: 'Kazakh', nativeName: 'Қазақша', flag: '🇰🇿', dayjsLocale: 'kk', dir: 'ltr' },
  { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', flag: '🇪🇹', dayjsLocale: 'am', dir: 'ltr' },
  { code: 'ha', name: 'Hausa', nativeName: 'Hausa', flag: '🇳🇬', dayjsLocale: 'en', dir: 'ltr' },
  { code: 'yo', name: 'Yoruba', nativeName: 'Yorùbá', flag: '🇳🇬', dayjsLocale: 'en', dir: 'ltr' },
  { code: 'zu', name: 'Zulu', nativeName: 'isiZulu', flag: '🇿🇦', dayjsLocale: 'en', dir: 'ltr' },
  { code: 'so', name: 'Somali', nativeName: 'Soomaali', flag: '🇸🇴', dayjsLocale: 'en', dir: 'ltr' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰', dayjsLocale: 'sk', dir: 'ltr' },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina', flag: '🇸🇮', dayjsLocale: 'sl', dir: 'ltr' },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių', flag: '🇱🇹', dayjsLocale: 'lt', dir: 'ltr' },
  { code: 'lv', name: 'Latvian', nativeName: 'Latviešu', flag: '🇱🇻', dayjsLocale: 'lv', dir: 'ltr' },
  { code: 'et', name: 'Estonian', nativeName: 'Eesti', flag: '🇪🇪', dayjsLocale: 'et', dir: 'ltr' },
  { code: 'sq', name: 'Albanian', nativeName: 'Shqip', flag: '🇦🇱', dayjsLocale: 'sq', dir: 'ltr' },
  { code: 'ka', name: 'Georgian', nativeName: 'ქართული', flag: '🇬🇪', dayjsLocale: 'ka', dir: 'ltr' },
  { code: 'mi', name: 'Maori', nativeName: 'Te Reo Māori', flag: '🇳🇿', dayjsLocale: 'mi', dir: 'ltr' },
  { code: 'mn', name: 'Mongolian', nativeName: 'Монгол', flag: '🇲🇳', dayjsLocale: 'mn', dir: 'ltr' },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ', flag: '🇮🇳', dayjsLocale: 'en', dir: 'ltr' },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];
export type LanguageDir = 'ltr' | 'rtl';

/** Check if a language code is RTL */
export function isRTL(code: string): boolean {
  const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  return lang?.dir === 'rtl';
}

const resources = {
  en: { translation: en },
  zh: { translation: zh },
  hi: { translation: hi },
  vi: { translation: vi },
  ms: { translation: ms },
  sm: { translation: sm },
  es: { translation: es },
  de: { translation: de },
  fr: { translation: fr },
  ar: { translation: ar },
  ru: { translation: ru },
  ja: { translation: ja },
  it: { translation: it },
  yue: { translation: yue },
  pa: { translation: pa },
  el: { translation: el },
  fil: { translation: fil },
  pt: { translation: pt },
  ko: { translation: ko },
  id: { translation: id },
  tr: { translation: tr },
  th: { translation: th },
  uk: { translation: uk },
  nl: { translation: nl },
  fi: { translation: fi },
  sv: { translation: sv },
  no: { translation: no },
  da: { translation: da },
  bn: { translation: bn },
  pl: { translation: pl },
  cs: { translation: cs },
  ur: { translation: ur },
  fa: { translation: fa },
  sw: { translation: sw },
  ta: { translation: ta },
  te: { translation: te },
  mr: { translation: mr },
  gu: { translation: gu },
  kn: { translation: kn },
  ml: { translation: ml },
  hu: { translation: hu },
  ro: { translation: ro },
  bg: { translation: bg },
  sr: { translation: sr },
  hr: { translation: hr },
  my: { translation: my },
  km: { translation: km },
  lo: { translation: lo },
  ne: { translation: ne },
  he: { translation: he },
  ca: { translation: ca },
  af: { translation: af },
  si: { translation: si },
  az: { translation: az },
  uz: { translation: uz },
  kk: { translation: kk },
  am: { translation: am },
  ha: { translation: ha },
  yo: { translation: yo },
  zu: { translation: zu },
  so: { translation: so },
  sk: { translation: sk },
  sl: { translation: sl },
  lt: { translation: lt },
  lv: { translation: lv },
  et: { translation: et },
  sq: { translation: sq },
  ka: { translation: ka },
  mi: { translation: mi },
  mn: { translation: mn },
  or: { translation: or },
};

/**
 * Sync dayjs locale and document direction with i18n language
 */
function syncLanguageSettings(lng: string) {
  const lang = SUPPORTED_LANGUAGES.find((l) => l.code === lng);
  if (lang) {
    // Sync dayjs locale
    dayjs.locale(lang.dayjsLocale);

    // Sync document direction for RTL languages
    document.documentElement.dir = lang.dir;
    document.documentElement.lang = lang.code;
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'booking-language',
    },
  });

// Set initial language settings (dayjs locale + document direction)
syncLanguageSettings(i18n.language);

// Update settings when language changes
i18n.on('languageChanged', syncLanguageSettings);

export default i18n;
